import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type {
  Artifact,
  ArtifactWorkItemLink,
  ArtifactWorkItemLinkKind,
  CurrentUser,
  GitHubWorkItem,
  GitHubWorkItemDetailResponse,
} from '@/lib/api';
import {
  createArtifactWorkItemLink,
  deleteArtifactWorkItemLink,
  findWorkItemByRepoIssue,
  getWorkItem,
  listArtifactWorkItemLinks,
} from '@/lib/github-work-items-api';

interface ArtifactWorkItemLinksPanelProps {
  artifact: Artifact;
  currentUser: CurrentUser | null;
}

interface IssueReference {
  owner: string;
  repo: string;
  issueNumber: number;
}

interface CreateLinkInput extends IssueReference {
  linkKind: ArtifactWorkItemLinkKind;
}

const ISSUE_NOT_FOUND_MESSAGE =
  'Issue not found in Metis cache. Open the Issue in GitHub once so the webhook seeds the read model, then retry.';

const LINK_KIND_LABEL: Record<ArtifactWorkItemLinkKind, string> = {
  tracks: 'tracks',
  imported_from: 'imported from',
  related: 'related',
};

const issueReferencePattern = /^\s*([^/\s#]+)\/([^/\s#]+)#([1-9]\d*)\s*$/;

function parseIssueReference(input: string): IssueReference | null {
  const match = issueReferencePattern.exec(input);
  if (!match) return null;

  return {
    owner: match[1],
    repo: match[2],
    issueNumber: Number(match[3]),
  };
}

function githubIssueUrl(workItem: GitHubWorkItem): string {
  return `https://github.com/${encodeURIComponent(workItem.githubRepoOwner)}/${encodeURIComponent(workItem.githubRepoName)}/issues/${String(workItem.githubIssueNumber)}`;
}

function repoIssueLabel(workItem: GitHubWorkItem): string {
  return `${workItem.githubRepoOwner}/${workItem.githubRepoName}#${String(workItem.githubIssueNumber)}`;
}

function mutationErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function ArtifactWorkItemLinksPanel({
  artifact,
  currentUser,
}: ArtifactWorkItemLinksPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const canMutate =
    currentUser !== null && (currentUser.role === 'admin' || currentUser.id === artifact.ownerId);
  const [issueReference, setIssueReference] = useState('');
  const [linkKind, setLinkKind] = useState<ArtifactWorkItemLinkKind>('tracks');
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const linksQuery = useQuery<ArtifactWorkItemLink[]>({
    queryKey: ['artifact', artifact.id, 'work-item-links'],
    queryFn: () => listArtifactWorkItemLinks(artifact.id),
  });

  const links = linksQuery.data ?? [];
  const workItemQueries = useQueries({
    queries: links.map(link => ({
      queryKey: ['github-work-item', link.workItemId] as const,
      queryFn: (): Promise<GitHubWorkItemDetailResponse> => getWorkItem(link.workItemId),
      staleTime: 30_000,
    })),
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateLinkInput): Promise<ArtifactWorkItemLink> => {
      const workItem = await findWorkItemByRepoIssue(input.owner, input.repo, input.issueNumber);
      if (!workItem) throw new Error(ISSUE_NOT_FOUND_MESSAGE);

      return createArtifactWorkItemLink(artifact.id, {
        workItemId: workItem.id,
        linkKind: input.linkKind,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['artifact', artifact.id, 'work-item-links'],
      });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setIssueReference('');
      setCreateError(null);
    },
    onError: error => {
      setCreateError(mutationErrorMessage(error, 'Failed to link work item.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (linkId: string) => deleteArtifactWorkItemLink(linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['artifact', artifact.id, 'work-item-links'],
      });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setDeleteError(null);
    },
    onError: error => {
      setDeleteError(mutationErrorMessage(error, 'Failed to remove work-item link.'));
    },
  });

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setCreateError(null);
    if (!canMutate) return;

    const parsed = parseIssueReference(issueReference);
    if (!parsed) {
      setCreateError('Enter a GitHub issue as owner/repo#42.');
      return;
    }

    createMutation.mutate({ ...parsed, linkKind });
  }

  if (linksQuery.isLoading) {
    return <div className="text-xs text-text-secondary">Loading work-item links…</div>;
  }

  if (linksQuery.isError) {
    return (
      <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
        {linksQuery.error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 text-sm">
      {links.length === 0 ? (
        <span className="text-xs text-text-secondary">No work-item links yet.</span>
      ) : (
        <ul className="flex flex-col gap-2">
          {links.map((link, idx) => {
            const workItemQuery = workItemQueries[idx];
            const workItem = workItemQuery.data?.workItem;
            const isRemoving = deleteMutation.isPending && deleteMutation.variables === link.id;

            return (
              <li
                key={link.id}
                className="flex items-center gap-2 rounded-md border border-border bg-surface/40 p-2"
              >
                <span className="rounded-md bg-surface-elevated px-2 py-0.5 font-mono text-xs text-text-secondary">
                  {LINK_KIND_LABEL[link.linkKind]}
                </span>
                <div className="min-w-0 flex-1">
                  {workItemQuery.isLoading && (
                    <span className="text-xs text-text-secondary">Loading work item…</span>
                  )}
                  {workItemQuery.isError && (
                    <span className="font-mono text-xs text-text-secondary">
                      {link.workItemId} (cannot read)
                    </span>
                  )}
                  {workItem && (
                    <>
                      <a
                        href={githubIssueUrl(workItem)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex max-w-full items-center gap-1.5 text-primary hover:underline"
                      >
                        <span className="truncate">{workItem.title}</span>
                        <ExternalLink className="size-3 shrink-0" />
                      </a>
                      <span className="block truncate font-mono text-xs text-text-secondary">
                        {repoIssueLabel(workItem)}
                      </span>
                    </>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!canMutate || deleteMutation.isPending}
                  onClick={(): void => {
                    setDeleteError(null);
                    deleteMutation.mutate(link.id);
                  }}
                  className="inline-flex items-center gap-1.5"
                >
                  <Trash2 className="size-4" />
                  {isRemoving ? 'Removing…' : 'Remove'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <form className="flex flex-col gap-2 border-t border-border pt-2" onSubmit={handleSubmit}>
        <span className="text-xs text-text-secondary">Add GitHub work-item link</span>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_minmax(10rem,12rem)_auto]">
          <input
            value={issueReference}
            onChange={event => {
              setIssueReference(event.target.value);
              setCreateError(null);
            }}
            placeholder="owner/repo#42"
            disabled={!canMutate || createMutation.isPending}
            className="min-w-0 rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
            aria-label="GitHub issue reference"
          />
          <select
            value={linkKind}
            onChange={event => {
              setLinkKind(event.target.value as ArtifactWorkItemLinkKind);
              setCreateError(null);
            }}
            disabled={!canMutate || createMutation.isPending}
            className="min-w-0 rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
            aria-label="Work-item link kind"
          >
            <option value="tracks">tracks</option>
            <option value="imported_from">imported_from</option>
            <option value="related">related</option>
          </select>
          <Button
            type="submit"
            size="sm"
            disabled={!canMutate || createMutation.isPending}
            className="inline-flex items-center justify-center gap-1.5"
          >
            <Plus className="size-4" />
            {createMutation.isPending ? 'Adding…' : 'Add'}
          </Button>
        </div>
        {createError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {createError}
          </div>
        )}
        {deleteError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {deleteError}
          </div>
        )}
      </form>
    </div>
  );
}
